import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import SettingsCard from '../../components/SettingsCard';
import AssetPicker from '../../components/AssetPicker';
import OrgContext from '@/src/Authentication/components/OrgContext';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useOrgs from '@/src/Orgs/components/hooks/useOrgs';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';

/*
 * Org profile — name, logo, and read-only facts. The first real org
 * edit surface: classic's "Edit Details" pointed at a route that never
 * existed. Saves via PUT /orgs/:id (added with this page).
 */

export default function OrgProfileV2() {
    const { org, orgId } = useUserOrg();
    const { setOrg } = useContext(OrgContext) as any;
    const { updateOrg } = useOrgs();
    const { getOrgDomain } = useWhitelabelSettings();
    const authenticatedApi = useAuthenticatedApi();

    const [name, setName] = useState<string>(org?.name ?? '');
    const [logo, setLogo] = useState<string>(org?.logo ?? '');
    const [savingName, setSavingName] = useState(false);
    const [savingLogo, setSavingLogo] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [domain, setDomain] = useState('');
    const [license, setLicense] = useState<string | null>(null);

    useEffect(() => {
        setName(org?.name ?? '');
        setLogo(org?.logo ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org?.id]);

    useEffect(() => {
        let mounted = true;
        getOrgDomain(org?.id)
            .then((base: string) => {
                if (mounted) setDomain(base ?? '');
            })
            .catch(() => {});
        /* license facts are GlobalAdmin-gated server-side — the row
           simply doesn't render for org admins */
        authenticatedApi
            .get(`/licenses/org/${orgId}`)
            .then((r) => {
                const active = (r.data ?? []).find((l: any) => !l.expired);
                if (mounted && active)
                    setLicense(active.licenseType?.name ?? active.name ?? null);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org?.id]);

    const save = async (body: { name: string; logo?: string | null }) => {
        const updated = await updateOrg(orgId, body).catch(() => null);
        if (updated?.id) {
            /* keep the shell (org mark, account menu) in sync */
            setOrg({ ...org, name: updated.name, logo: updated.logo });
            toast.success('Saved');
            return true;
        }
        toast.error("Couldn't save — try again.");
        return false;
    };

    const nameDirty = name.trim() !== (org?.name ?? '') && name.trim() !== '';
    const logoDirty = (logo || '') !== (org?.logo ?? '');

    return (
        <>
            <SettingsCard
                title="Organization name"
                desc="Shown across the dashboard and in gallery emails."
                foot={
                    <>
                        <span className="flex-1 text-[11.5px] text-faint">
                            {name.trim() === ''
                                ? 'a name is required'
                                : ' '}
                        </span>
                        <button
                            type="button"
                            disabled={!nameDirty || savingName}
                            onClick={async () => {
                                setSavingName(true);
                                await save({ name: name.trim() });
                                setSavingName(false);
                            }}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background disabled:opacity-40"
                        >
                            {savingName ? 'Saving…' : 'Save'}
                        </button>
                    </>
                }
            >
                <input
                    className="ps-input w-full max-w-[420px] text-[13.5px]"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </SettingsCard>

            <SettingsCard
                title="Logo"
                desc="Used anywhere the organization is shown."
                foot={
                    <>
                        <span className="flex-1 text-[11.5px] text-faint">
                            square image recommended
                        </span>
                        <button
                            type="button"
                            disabled={!logoDirty || savingLogo}
                            onClick={async () => {
                                setSavingLogo(true);
                                await save({
                                    name: (org?.name ?? '').trim() || name.trim(),
                                    logo: logo || null,
                                });
                                setSavingLogo(false);
                            }}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background disabled:opacity-40"
                        >
                            {savingLogo ? 'Saving…' : 'Save'}
                        </button>
                    </>
                }
            >
                <div className="flex items-center gap-3.5">
                    <span className="flex h-[52px] w-[52px] flex-none items-center justify-center overflow-hidden rounded-[12px] border border-border bg-sunk text-[19px] font-extrabold tracking-[-0.03em]">
                        {logo ? (
                            <img
                                src={logo}
                                alt=""
                                className="h-full w-full object-contain"
                            />
                        ) : (
                            (name || 'P')[0].toUpperCase()
                        )}
                    </span>
                    <button
                        type="button"
                        onClick={() => setPickerOpen(true)}
                        className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                    >
                        Browse
                    </button>
                    {logo && (
                        <button
                            type="button"
                            onClick={() => setLogo('')}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-faint hover:text-attention"
                        >
                            Remove
                        </button>
                    )}
                </div>
            </SettingsCard>

            <SettingsCard
                title="Organization"
                desc="Read-only facts about this org."
            >
                <div className="max-w-[440px]">
                    <div className="flex items-baseline gap-3 border-b border-line py-2 text-[12.5px]">
                        <span className="w-[110px] flex-none text-muted-foreground">
                            Org ID
                        </span>
                        <span className="font-mono text-[11px]">{orgId}</span>
                    </div>
                    <div className="flex items-baseline gap-3 border-b border-line py-2 text-[12.5px] last:border-b-0">
                        <span className="w-[110px] flex-none text-muted-foreground">
                            Gallery domain
                        </span>
                        <span className="min-w-0 truncate font-mono text-[11px]">
                            {domain
                                ? domain.replace(/^https?:\/\//, '')
                                : '—'}
                        </span>
                        <span className="text-[11.5px] text-faint">
                            set in Whitelabel
                        </span>
                    </div>
                    {license && (
                        <div className="flex items-baseline gap-3 py-2 text-[12.5px]">
                            <span className="w-[110px] flex-none text-muted-foreground">
                                License
                            </span>
                            <span className="font-mono text-[11px] uppercase">
                                {license}
                            </span>
                        </div>
                    )}
                </div>
            </SettingsCard>

            {pickerOpen && (
                <AssetPicker
                    onSelect={(url) => {
                        setLogo(url);
                        setPickerOpen(false);
                    }}
                    onClose={() => setPickerOpen(false)}
                />
            )}
        </>
    );
}
